import React, { useState } from 'react';
import type { ReactNode } from 'react';

import { IconPencil, PencilIcon, TrashBinIcon } from '../../../assets/icons/icons';
import { Button } from '../../ui/Button/Button';
import type { QueueItem } from '../../../types/sizes';
import { formatDate } from '../../../utils/access';
import { DimensionValue } from '../DimensionValue/DimensionValue';
import { WeightValue } from '../WeightValue/WeightValue';
import { TransportNumber } from '../TransportNumber/TransportNumber';
import styles from './QueueTable.module.scss';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { DataTable, tableStyles } from '../../ui/DataTable/DataTable';
import { CatalogThumb } from '../../ui/CatalogThumb/CatalogThumb';
import { cx } from '../../../utils/cx';

interface QueueTableProps {
    items: QueueItem[];
    startIndex?: number;
    footer?: ReactNode;
    empty?: ReactNode;
    isLoading?: boolean;
    loading?: boolean;
    loadingText?: string;
    scrollKey?: string | number;
    onAdd: (item: QueueItem, values?: Record<string, number | null>) => void;
    onDelete: (item: QueueItem) => void;
}

function hostOf(url: string | null): string | null {
    if (!url) return null;

    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
}

const NUM_FIELDS = ['length', 'width', 'height', 'weight'] as const;
type NumField = (typeof NUM_FIELDS)[number];

const TRANSPORT_KEY: Record<NumField, keyof QueueItem> = {
    length: 'transportLength',
    width: 'transportWidth',
    height: 'transportHeight',
    weight: 'transportWeight',
};

const PATCH_KEY: Record<NumField, string> = {
    length: 'length',
    width: 'width',
    height: 'height',
    weight: 'maximum_load_weight',
};

const TRANSPORT_PATCH_KEY: Record<NumField, string> = {
    length: 'transport_length',
    width: 'transport_width',
    height: 'transport_height',
    weight: 'transport_weight',
};

const EMPTY_DRAFT: Record<NumField, string> = {
    length: '',
    width: '',
    height: '',
    weight: '',
};

export function QueueTable({
    items,
    startIndex = 0,
    footer,
    empty,
    isLoading,
    loading,
    loadingText,
    scrollKey,
    onAdd,
    onDelete
}: QueueTableProps) {
    const [editingId, setEditingId] = useState<string | null>(null);

    const [draft, setDraft] = useState<Record<NumField, string>>(EMPTY_DRAFT);
    const [tDraft, setTDraft] = useState<Record<NumField, string>>(EMPTY_DRAFT);

    const startEdit = (item: QueueItem) => {
        setDraft({
            length: String(item.length ?? ''),
            width: String(item.width ?? ''),
            height: String(item.height ?? ''),
            weight: String(item.weight ?? ''),
        });
        setTDraft({
            length: String(item.transportLength ?? ''),
            width: String(item.transportWidth ?? ''),
            height: String(item.transportHeight ?? ''),
            weight: String(item.transportWeight ?? ''),
        });
        setEditingId(item.id);
    };

    const isBaseChanged = (item: QueueItem) =>
        NUM_FIELDS.some((field) => (Number(draft[field]) || null) !== (item[field] ?? null));

    const isTransportChanged = (item: QueueItem) =>
        NUM_FIELDS.some(
            (field) =>
                (Number(tDraft[field]) || null) !==
                ((item[TRANSPORT_KEY[field]] as number | null) ?? null),
        );

    const isChanged = (item: QueueItem) => isBaseChanged(item) || isTransportChanged(item);

    const transportFilled = (item: QueueItem): boolean => {
        const source =
            editingId === item.id
                ? NUM_FIELDS.map((field) => Number(tDraft[field]) || 0)
                : NUM_FIELDS.map((field) => (item[TRANSPORT_KEY[field]] as number | null) ?? 0);

        return source.every((value) => value > 0);
    };

    const add = (item: QueueItem) => {
        const edited = editingId === item.id && isChanged(item);
        const num = (raw: string) => Number(raw) || null;

        const values: Record<string, number | null> = {};

        if (edited && isBaseChanged(item)) {
            NUM_FIELDS.forEach((field) => { values[PATCH_KEY[field]] = num(draft[field]); });
        }
        if (edited && isTransportChanged(item)) {
            NUM_FIELDS.forEach((field) => { values[TRANSPORT_PATCH_KEY[field]] = num(tDraft[field]); });
        }

        onAdd(item, edited ? values : undefined);
        setEditingId(null);
    };

    const labelCell = () => (
        <td className={`${styles.number} ${styles.labels}`}>
            <span className={styles.stack}>
                <span className={styles.main}>
                    <i>эталон</i>
                </span>
                <span className={styles.transport}>
                    <i>трансп.</i>
                </span>
            </span>
        </td>
    );

    const numCell = (item: QueueItem, field: NumField) => (
        <td className={`${tableStyles.num} ${styles.number}`}>
            {editingId === item.id ? (
                <span className={styles.stack}>
                    <span className={styles.main}>
                        <input
                            className={styles.input}
                            inputMode="numeric"
                            aria-label={`${field}, эталон`}
                            value={draft[field]}
                            onChange={(event) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    [field]: event.target.value.replace(/\D/g, ''),
                                }))
                            }
                        />
                    </span>

                    <span className={styles.transport}>
                        <input
                            className={styles.input}
                            inputMode="numeric"
                            aria-label={`${field}, транспортный`}
                            value={tDraft[field]}
                            onChange={(event) =>
                                setTDraft((prev) => ({
                                    ...prev,
                                    [field]: event.target.value.replace(/\D/g, ''),
                                }))
                            }
                        />
                    </span>
                </span>
            ) : (
                <span className={styles.stack}>
                    <span className={styles.main}>
                        {field === 'weight' ? (
                            <WeightValue grams={item.weight} />
                        ) : (
                            <DimensionValue value={item[field]} />
                        )}
                    </span>

                    <span
                        className={styles.transport}
                        title={
                            item.transportIsManual
                                ? 'Транспортный габарит, задан вручную'
                                : 'Транспортный габарит'
                        }
                    >
                        <TransportNumber value={item[TRANSPORT_KEY[field]] as number | null} />
                    </span>
                </span>
            )}
        </td>
    );

    return (
        <DataTable
            static
            colgroup={
                <colgroup>
                    <col style={{ width:36 }} />     
                    <col style={{ width: '30%' }} />  
                    <col style={{ width: '16%' }} />  
                    <col style={{ width: 64 }} />    
                    <col style={{ width: '10%' }} /> 
                    <col style={{ width: '10%' }} />  
                    <col style={{ width: '10%' }} />  
                    <col style={{ width: '10%' }} /> 
                    <col style={{ width: '14%' }} />  
                    <col style={{ width: 190 }} />   
                </colgroup>
            }
            footer={footer}
            empty={empty}
            isLoading={isLoading}
            loading={loading}
            loadingText={loadingText}
            scrollKey={scrollKey}
        >
            <thead>
                <tr>
                    <th className={tableStyles.index}>№</th>
                    <th className={styles.numeCol}>Наименование</th>
                    <th className={styles.projectCol}>Проект</th>

                    <th />

                    <th className={styles.number}>Длина, мм</th>
                    <th className={styles.number}>Ширина, мм</th>
                    <th className={styles.number}>Высота, мм</th>
                    <th className={styles.number}>Вес, г</th>
                    <th className={styles.number}>Найден</th>
                    <th className={cx(tableStyles.actions, styles.actionsCol)} aria-label="Действия" />
                </tr>
            </thead>

            <tbody>
                {items.map((item, index) => {
                    const host = hostOf(item.url);

                    return (
                        <tr key={item.id}>
                            <td className={tableStyles.index}>{startIndex + index + 1}</td>
                            <td className={styles.name}>
                                <div className={styles.nameInner}>
                                    <CatalogThumb src={item.imageUrl} />

                                    <div className={styles.nameText}>
                                        {item.name}

                                        {host && (
                                            <span className={styles.source}>
                                                <a href={item.url!} target="_blank" rel="noopener noreferrer">{host}</a>
                                            </span>
                                        )}

                                        {item.sourceTitles.length > 0 && (
                                            <Tooltip text={item.sourceTitles.join('\n')} align="left">
                                                <span className={styles.source}>{item.sourceTitles[0]}</span>
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                            </td>

                            <td className={styles.project}>
                                <span className={styles.source}>
                                    {item.projectName === '—' || item.projectName === '' ? 'Отсутствует' : item.projectName}
                                </span>
                            </td>

                            {labelCell()}
                            {numCell(item, 'length')}
                            {numCell(item, 'width')}
                            {numCell(item, 'height')}
                            {numCell(item, 'weight')}

                            <td className={`${tableStyles.num} ${styles.number}`}>
                                {formatDate(item.createdAt)}
                            </td>

                            <td className={tableStyles.actions}>
                                {editingId === item.id ? (
                                    <span className={styles.editActions}>
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={!transportFilled(item)}
                                            onClick={() => add(item)}
                                        >
                                            Сохранить
                                        </Button>
                                        <Button size="sm" onClick={() => setEditingId(null)}>
                                            Отмена
                                        </Button>
                                    </span>
                                ) : (
                                    <span className={styles.actions}>
                                        <Tooltip
                                            text={transportFilled(item) ? '' : 'Заполните транспортные габариты: три размера и вес'}
                                            align="right"
                                        >
                                            <span className={styles.tipWrap}>
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    disabled={!transportFilled(item)}
                                                    onClick={() => add(item)}
                                                >
                                                    В справочник
                                                </Button>
                                            </span>
                                        </Tooltip>

                                        <Tooltip text="Изменить габариты" align="right">
                                            <button
                                                type="button"
                                                className={styles.iconBtn}
                                                aria-label="Изменить габариты"
                                                onClick={() => startEdit(item)}
                                            >
                                                <PencilIcon />
                                            </button>
                                        </Tooltip>

                                        <Tooltip text="Убрать из очереди" align="right">
                                            <button
                                                type="button"
                                                className={`${styles.iconBtn} ${styles.danger}`}
                                                aria-label="Убрать из очереди"
                                                onClick={() => onDelete(item)}
                                            >
                                                <TrashBinIcon />
                                            </button>
                                        </Tooltip>
                                    </span>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </DataTable >
    );
}